import React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Collapse, IconButton, ListItemButton, Paper, Chip } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import GroupIcon from "@mui/icons-material/Group";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { makeStyles } from "@mui/styles";
import { useHistory } from "react-router";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SettingsIcon from "@mui/icons-material/Settings";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { getUser, hasAnyRole, removeUserSession } from "../Utils/Common";
import { useEffect } from "react";
import { getUnreadNotificationCount, getUserNotificationData } from "../services/Notification";
import CloseIcon from "@mui/icons-material/Close";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { RoleBasedRender } from "./RoleBaseRender";
import MenuIcon from "@mui/icons-material/Menu";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useThemeMode } from "../contexts/ThemeContext";

const drawerWidth = 240;

const useStyles = makeStyles({
  page: {
    marginTop: "8vh",
  },
});

// Menu configuration - Easy to modify, add, or remove items
const MENU_CONFIG = [
  {
    id: "home",
    label: "Home",
    icon: HomeIcon,
    path: "/template/home",
    roles: [
      "ROLE_ADMIN",
      "MEMBER",
      "WT_BILL_VIEW",
      "WT_BILL_CREATE",
      "WT_BILL_APPROVE",
      "WT_BILL_PAYMENT_UPDATE",
      "WT_ITEM_TRANSFER",
      "WT_BILL_DELETE",
      "SALE_PRODUCT_VIEW",
      "SALE_PRODUCT_CREATE",
      "SALE_PRODUCT_UPDATE",
      "SALE_PRODUCT_DELETE",
      "SALE_BILL_VIEW",
      "SALE_BILL_CREATE",
      "SALE_BILL_APPROVE",
      "SALE_BILL_DELETE",
      "SALE_BILL_PAYMENT_UPDATE",
      "INVENTORY_VIEW",
      "INVENTORY_UPDATE",
      "INVENTORY_CREATE",
      "INVENTORY_DELETE",
      "IMPORT_STOCK_VIEW",
      "IMPORT_STOCK_UPDATE",
      "IMPORT_STOCK_CREATE",
      "IMPORT_STOCK_DELETE",
      "IMPORT_BILL_VIEW",
      "IMPORT_BILL_UPDATE",
      "IMPORT_BILL_CREATE",
      "IMPORT_BILL_DELETE",
      "IMPORT_BILL_APPROVE",
      "IMPORT_BILL_RETURN_VIEW",
      "IMPORT_BILL_RETURN_UPDATE",
      "IMPORT_BILL_RETURN_CREATE",
      "IMPORT_BILL_RETURN_DELETE",
      "IMPORT_BILL_RETURN_APPROVE",
      "PRODUCT_VIEW",
      "PRODUCT_UPDATE",
      "PRODUCT_CREATE",
      "PRODUCT_DELETE",
      "PRODUCTION_VIEW",
      "PRODUCTION_CREATE",
      "PRODUCTION_UPDATE",
      "PRODUCTION_DELETE",
      "WAREHOUSE_CREATE",
      "WAREHOUSE_VIEW",
      "WAREHOUSE_UPDATE",
      "WAREHOUSE_DELETE",
      "CUSTOMER_VIEW",
      "CUSTOMER_CREATE",
      "CUSTOMER_UPDATE",
      "CUSTOMER_DISABLE",
      "SALESMAN_VIEW",
      "SUB_ORDER_PERSON_CREATE",
      "SUB_ORDER_PERSON_VIEW",
      "SUB_ORDER_PERSON_EDIT",
      "SUB_ORDER_PERSON_DELETE",
      "SUB_ORDER_CREATE",
      "SUB_ORDER_VIEW",
      "SUB_ORDER_EDIT",
      "SUB_ORDER_DELETE",
    ],
  },
  {
    id: "import",
    label: "Import",
    icon: ArrowCircleDownIcon,
    roles: [
      "ROLE_ADMIN",
      "INVENTORY_VIEW",
      "INVENTORY_UPDATE",
      "INVENTORY_CREATE",
      "INVENTORY_DELETE",
      "IMPORT_STOCK_VIEW",
      "IMPORT_STOCK_UPDATE",
      "IMPORT_STOCK_CREATE",
      "IMPORT_STOCK_DELETE",
      "IMPORT_BILL_VIEW",
      "IMPORT_BILL_UPDATE",
      "IMPORT_BILL_CREATE",
      "IMPORT_BILL_DELETE",
      "SUB_ORDER_PERSON_CREATE",
      "SUB_ORDER_PERSON_VIEW",
      "SUB_ORDER_PERSON_EDIT",
      "SUB_ORDER_PERSON_DELETE",
      "SUB_ORDER_CREATE",
      "SUB_ORDER_VIEW",
      "SUB_ORDER_EDIT",
      "SUB_ORDER_DELETE",
      "INVENTORY_PRICE_VIEW",
      "INVENTORY_PRICE_CREATE",
      "INVENTORY_PRICE_UPDATE",
      "INVENTORY_PRICE_DELETE",
    ],
    children: [
      {
        id: "import-items",
        label: "Items",
        path: "/template/im_items",
        roles: [
          "ROLE_ADMIN",
          "INVENTORY_VIEW",
          "INVENTORY_UPDATE",
          "INVENTORY_CREATE",
          "INVENTORY_DELETE",
        ],
      },
      {
        id: "import-prices",
        label: "Prices",
        path: "/template/im_prices",
        roles: [
          "ROLE_ADMIN",
          "INVENTORY_PRICE_VIEW",
          "INVENTORY_PRICE_CREATE",
          "INVENTORY_PRICE_UPDATE",
          "INVENTORY_PRICE_DELETE",
        ],
      },
      {
        id: "import-categories",
        label: "Categories",
        path: "/template/im_categories",
        roles: [
          "ROLE_ADMIN",
          "INVENTORY_VIEW",
          "INVENTORY_UPDATE",
          "INVENTORY_CREATE",
          "INVENTORY_DELETE",
        ],
      },
      {
        id: "import-bills",
        label: "Bills",
        path: "/template/im_bills",
        roles: [
          "ROLE_ADMIN",
          "IMPORT_BILL_VIEW",
          "IMPORT_BILL_UPDATE",
          "IMPORT_BILL_CREATE",
          "IMPORT_BILL_DELETE",
        ],
      },
      {
        id: "import-stock",
        label: "Stock Updates",
        path: "/template/im_stockupdates",
        roles: [
          "ROLE_ADMIN",
          "IMPORT_STOCK_VIEW",
          "IMPORT_STOCK_UPDATE",
          "IMPORT_STOCK_CREATE",
          "IMPORT_STOCK_DELETE",
        ],
      },
      {
        id: "sub-order-product",
        label: "Sub Order Product",
        path: "/template/sub_order_product",
        roles: [
          "ROLE_ADMIN",
          "SUB_ORDER_PERSON_CREATE",
          "SUB_ORDER_PERSON_VIEW",
          "SUB_ORDER_PERSON_EDIT",
          "SUB_ORDER_PERSON_DELETE",
        ],
      },
      {
        id: "sub-order-person",
        label: "Sub Order Person",
        path: "/template/sub_order_person",
        roles: [
          "ROLE_ADMIN",
          "SUB_ORDER_PERSON_CREATE",
          "SUB_ORDER_PERSON_VIEW",
          "SUB_ORDER_PERSON_EDIT",
          "SUB_ORDER_PERSON_DELETE",
        ],
      },
      {
        id: "sub-order",
        label: "Sub Order",
        path: "/template/sub_order",
        roles: [
          "ROLE_ADMIN",
          "SUB_ORDER_CREATE",
          "SUB_ORDER_VIEW",
          "SUB_ORDER_EDIT",
          "SUB_ORDER_DELETE",
        ],
      },
    ],
  },
  {
    id: "export",
    label: "Export",
    icon: ArrowCircleUpIcon,
    roles: [
      "ROLE_ADMIN",
      "PRODUCT_VIEW",
      "PRODUCT_UPDATE",
      "PRODUCT_CREATE",
      "PRODUCT_DELETE",
      "PRODUCTION_VIEW",
      "PRODUCTION_CREATE",
      "PRODUCTION_UPDATE",
      "PRODUCTION_DELETE",
      "WAREHOUSE_CREATE",
      "WAREHOUSE_VIEW",
      "WAREHOUSE_UPDATE",
      "WAREHOUSE_DELETE",
      "PRODUCT_PRICE_VIEW",
      "PRODUCT_PRICE_UPDATE",
      "PRODUCT_PRICE_CREATE",
      "PRODUCT_PRICE_DELETE",
      "MATERIALS_FOR_PRODUCT_VIEW",
      "MATERIALS_FOR_PRODUCT_UPDATE",
      "MATERIALS_FOR_PRODUCT_CREATE",
      "MATERIALS_FOR_PRODUCT_DELETE",
      "PRODUCT_COST_VIEW",
      "PRODUCT_COST_UPDATE",
      "PRODUCT_COST_CREATE",
      "PRODUCT_COST_DELETE",
      "LABEL_CREATE",
      "LABEL_PRINT",
    ],
    children: [
      {
        id: "export-products",
        label: "Products",
        path: "/template/ex_items",
        roles: [
          "ROLE_ADMIN",
          "PRODUCT_VIEW",
          "PRODUCT_UPDATE",
          "PRODUCT_CREATE",
          "PRODUCT_DELETE",
        ],
      },
      {
        id: "export-categories",
        label: "Categories",
        path: "/template/ex_categories",
        roles: [
          "ROLE_ADMIN",
          "PRODUCT_VIEW",
          "PRODUCT_UPDATE",
          "PRODUCT_CREATE",
          "PRODUCT_DELETE",
        ],
      },
      {
        id: "export-product-prices",
        label: "Product Prices",
        path: "/template/ex_product_prices",
        roles: [
          "ROLE_ADMIN",
          "PRODUCT_PRICE_VIEW",
          "PRODUCT_PRICE_UPDATE",
          "PRODUCT_PRICE_CREATE",
          "PRODUCT_PRICE_DELETE",
        ],
      },
      {
        id: "export-production",
        label: "Production",
        path: "/template/ex_production",
        roles: [
          "ROLE_ADMIN",
          "PRODUCTION_VIEW",
          "PRODUCTION_CREATE",
          "PRODUCTION_UPDATE",
          "PRODUCTION_DELETE",
        ],
      },
      {
        id: "export-label-printing",
        label: "Label Printing",
        path: "/template/label-designer",
        roles: ["ROLE_ADMIN", "LABEL_CREATE", "LABEL_PRINT"],
      },
      {
        id: "export-warehouses",
        label: "Warehouses",
        path: "/template/warehouses",
        roles: [
          "ROLE_ADMIN",
          "WAREHOUSE_CREATE",
          "WAREHOUSE_VIEW",
          "WAREHOUSE_UPDATE",
          "WAREHOUSE_DELETE",
        ],
      },
    ],
  },
  {
    id: "sales",
    label: "Sales",
    icon: AttachMoneyIcon,
    roles: [
      "ROLE_ADMIN",
      "SALE_PRODUCT_VIEW",
      "SALE_PRODUCT_CREATE",
      "SALE_PRODUCT_UPDATE",
      "SALE_PRODUCT_DELETE",
      "SALE_BILL_VIEW",
      "SALE_BILL_CREATE",
      "SALE_BILL_APPROVE",
      "SALE_BILL_DELETE",
    ],
    children: [
      {
        id: "sales-products",
        label: "Sale Products",
        path: "/template/sale_products",
        roles: [
          "ROLE_ADMIN",
          "SALE_PRODUCT_VIEW",
          "SALE_PRODUCT_CREATE",
          "SALE_PRODUCT_UPDATE",
          "SALE_PRODUCT_DELETE",
        ],
      },
      {
        id: "sales-bills",
        label: "Sale Bills",
        path: "/template/sale_bills",
        roles: [
          "ROLE_ADMIN",
          "SALE_BILL_VIEW",
          "SALE_BILL_CREATE",
          "SALE_BILL_APPROVE",
          "SALE_BILL_DELETE",
        ],
      },
    ],
  },
  {
    id: "customers",
    label: "Customers & Routes",
    icon: GroupIcon,
    roles: [
      "ROLE_ADMIN",
      "CUSTOMER_VIEW",
      "CUSTOMER_CREATE",
      "CUSTOMER_UPDATE",
      "CUSTOMER_DISABLE",
      "ROUTE_VIEW",
      "ROUTE_CREATE",
      "ROUTE_UPDATE",
      "ROUTE_DELETE",
    ],
    children: [
      {
        id: "customers-list",
        label: "Customers",
        path: "/template/customers",
        roles: [
          "ROLE_ADMIN",
          "CUSTOMER_VIEW",
          "CUSTOMER_CREATE",
          "CUSTOMER_UPDATE",
          "CUSTOMER_DISABLE",
        ],
      },
      {
        id: "routes-list",
        label: "Routes",
        path: "/template/routes",
        roles: [
          "ROLE_ADMIN",
          "ROUTE_VIEW",
          "ROUTE_CREATE",
          "ROUTE_UPDATE",
          "ROUTE_DELETE",
        ],
      },
    ],
  },
  {
    id: "retail",
    label: "Retail",
    icon: AddBusinessIcon,
    roles: [
      "ROLE_ADMIN",
      "WT_BILL_VIEW",
      "WT_BILL_CREATE",
      "WT_BILL_APPROVE",
      "WT_BILL_DELETE",
    ],
    children: [
      {
        id: "retail-salesman",
        label: "Sales Person",
        path: "/template/salesman",
        roles: [
          "ROLE_ADMIN",
          "WT_BILL_VIEW",
          "WT_BILL_CREATE",
          "WT_BILL_APPROVE",
          "WT_BILL_DELETE",
        ],
      },
      {
        id: "retail-bills",
        label: "Warehouse Transfer Bill",
        path: "/template/retail_sale_bills",
        roles: [
          "ROLE_ADMIN",
          "WT_BILL_VIEW",
          "WT_BILL_CREATE",
          "WT_BILL_APPROVE",
          "WT_BILL_DELETE",
        ],
      },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: AssessmentIcon,
    path: "/template/report-builder",
    roles: ["ROLE_ADMIN", "REPORT_CREATE", "REPORT_GENERATE"],
  },
  {
    id: "user-management",
    label: "User Management",
    icon: ManageAccountsIcon,
    roles: ["ROLE_ADMIN"],
    children: [
      {
        id: "users",
        label: "Users",
        path: "/template/users",
        roles: ["ROLE_ADMIN"],
      },
    ],
  },
  {
    id: "app-settings",
    label: "App Settings",
    icon: SettingsIcon,
    roles: ["ROLE_ADMIN"],
    children: [
      {
        id: "settings",
        label: "Settings",
        path: "/template/settings",
        roles: ["ROLE_ADMIN", "VIEW_SETTINGS", "EDIT_SETTINGS"],
      },
    ],
  },
];

function Template({ children }) {
  const history = useHistory();
  const classes = useStyles();
  const menuId = "primary-search-account-menu";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { toggleDarkMode } = useThemeMode();

  const isDarkMode = theme.palette.mode === "dark";

  const themeColors = {
    primary: "#3F6C9E",
    secondary: "#C62828",
    selectedBg: isDarkMode ? "#1a3050" : "#E6EDF7",
    selectedText: "#3F6C9E",
    appBarBg: isDarkMode ? "#1a1a2e" : "#ffffff",
    drawerBg: isDarkMode ? "#1e1e1e" : "#ffffff",
    textColor: isDarkMode ? "#e0e0e0" : "#000000",
  };

  // State management
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [expandedMenus, setExpandedMenus] = React.useState({});
  const [selectedItem, setSelectedItem] = React.useState("home");
  const [user, setUser] = React.useState("");
  const [notificationCount, setNotificationCount] = React.useState(0);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showPopup, setShowPopup] = React.useState(false);
  const [popupNotification, setPopupNotification] = React.useState(null);
  const popupTimerRef = React.useRef(null);
  const prevCountRef = React.useRef(null);

  const isMenuOpen = Boolean(anchorEl);

  // Handle drawer toggle for mobile
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Handle menu expansion/collapse
  const handleMenuToggle = (menuId) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  // Handle menu item click
  const handleMenuItemClick = (item, parentId = null) => {
    const itemId = parentId ? `${parentId}-${item.id}` : item.id;
    setSelectedItem(itemId);

    if (item.path) {
      history.push(item.path);
    }

    // Close mobile drawer after navigation
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Check if item or any of its children is selected
  const isItemSelected = (item, parentId = null) => {
    const itemId = parentId ? `${parentId}-${item.id}` : item.id;

    if (selectedItem === itemId) {
      return true;
    }

    // Check if any child is selected
    if (item.children) {
      return item.children.some(
        (child) => selectedItem === `${itemId}-${child.id}`,
      );
    }

    return false;
  };

  // User profile menu handlers
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClosePopup = () => {
    clearTimeout(popupTimerRef.current);
    setShowPopup(false);
  };

  const userLogout = () => {
    removeUserSession();
    history.push("/");
  };

  // Handle mobile logout
  const handleMobileLogout = () => {
    setMobileOpen(false);
    userLogout();
  };

  // Initialize user data
  useEffect(() => {
    const initializeUserData = async () => {
      try {
        const userData = getUser();
        setUser(userData.fname + " " + userData.lname);
      } catch (error) {
        console.error("Error initializing user data:", error);
      }
    };

    initializeUserData();
  }, []);

  // Continuously track unread notification count and show popup on new arrivals
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const result = await getUnreadNotificationCount();
        if (result !== "Something went wrong...") {
          if (prevCountRef.current !== null && result > prevCountRef.current) {
            const pageData = await getUserNotificationData(0, 1, { type: null, status: "UNREAD" });
            if (pageData?.content?.length > 0) {
              setPopupNotification(pageData.content[0]);
              setShowPopup(true);
              clearTimeout(popupTimerRef.current);
              popupTimerRef.current = setTimeout(() => setShowPopup(false), 4500);
            }
          }
          prevCountRef.current = result;
          setNotificationCount(result);
        }
      } catch (error) {
        console.error("Error fetching notification count:", error);
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    const unlisten = history.listen(fetchCount);

    return () => {
      clearInterval(interval);
      unlisten();
      clearTimeout(popupTimerRef.current);
    };
  }, [history]);

  // Set initial selected item based on current path
  useEffect(() => {
    const currentPath = history.location.pathname;

    // Find matching menu item based on current path
    const findSelectedItem = (items, parentId = null) => {
      for (const item of items) {
        const itemId = parentId ? `${parentId}-${item.id}` : item.id;

        if (item.path === currentPath) {
          setSelectedItem(itemId);
          // Expand parent menu if it's a child item
          if (parentId) {
            setExpandedMenus((prev) => ({ ...prev, [parentId]: true }));
          }
          return;
        }

        if (item.children) {
          findSelectedItem(item.children, itemId);
        }
      }
    };

    findSelectedItem(MENU_CONFIG);
  }, [history.location.pathname]);

  const text = {
    fontSize: "15px",
    fontWeight: "bold",
  };

  const sub = {
    fontSize: "15px",
  };

  // Render menu item
  const renderMenuItem = (item, level = 0, parentId = null) => {
    const itemId = parentId ? `${parentId}-${item.id}` : item.id;
    const isSelected = isItemSelected(item, parentId);
    const isExpanded = expandedMenus[itemId];
    const IconComponent = item.icon;

    const menuItem = (
      <ListItemButton
        key={itemId}
        sx={{ pl: level * 2 + 2 }}
        selected={isSelected}
        onClick={() => {
          if (item.children) {
            handleMenuToggle(itemId);
          } else {
            handleMenuItemClick(item, parentId);
          }
        }}
        style={{
          backgroundColor: isSelected ? themeColors.selectedBg : themeColors.drawerBg,
          color: isSelected ? themeColors.selectedText : themeColors.textColor,
        }}
      >
        {IconComponent && (
          <ListItemIcon>
            <IconComponent style={{ color: themeColors.primary }} />
          </ListItemIcon>
        )}
        <ListItemText
          primaryTypographyProps={{ style: level === 0 ? text : sub }}
          primary={item.label}
        />
        {item.children && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>
    );

    const childItems = item.children && (
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {item.children.map((child) => (
            <RoleBasedRender
              key={`${itemId}-${child.id}`}
              user={user}
              allowedRoles={child.roles}
            >
              {renderMenuItem(child, level + 1, itemId)}
            </RoleBasedRender>
          ))}
        </List>
      </Collapse>
    );

    return (
      <React.Fragment key={itemId}>
        {menuItem}
        {childItems}
      </React.Fragment>
    );
  };

  // Profile menu
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
      <MenuItem onClick={handleMenuClose}>My Account</MenuItem>
      <MenuItem onClick={userLogout}>Log Out</MenuItem>
    </Menu>
  );

  // Main drawer content
  const drawer = (
    <>
      <Toolbar />
      <Box sx={{ overflow: "auto" }}>
        <List>
          {MENU_CONFIG.map((item) => (
            <RoleBasedRender
              key={item.id}
              user={user}
              allowedRoles={item.roles}
            >
              {renderMenuItem(item)}
            </RoleBasedRender>
          ))}
        </List>
        <Divider />

        {/* Mobile-only user info and logout */}
        {isMobile && (
          <List>
            <ListItemButton>
              <ListItemIcon>
                <AccountCircle style={{ color: themeColors.primary }} />
              </ListItemIcon>
              <ListItemText
                primary={user}
                primaryTypographyProps={{ style: { fontSize: "14px" } }}
              />
            </ListItemButton>
            <ListItemButton onClick={handleMobileLogout}>
              <ListItemIcon>
                <LogoutIcon style={{ color: themeColors.secondary }} />
              </ListItemIcon>
              <ListItemText
                primary="Log Out"
                primaryTypographyProps={{ style: text }}
              />
            </ListItemButton>
          </List>
        )}
      </Box>
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        style={{ backgroundColor: themeColors.appBarBg }}
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, color: themeColors.primary }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            component="img"
            sx={{
              height: 25,
              margin: isMobile ? "0 auto" : 0,
              maxHeight: { xs: 233, md: 167 },
              maxWidth: { xs: 350, md: 250 },
            }}
            alt="logo"
            src="../images/logo.png"
          />

          {/* Spacer — pushes icons to the right on both mobile and desktop */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Dark/Light mode toggle — visible on ALL screen sizes */}
          <IconButton
            size="large"
            aria-label="toggle dark mode"
            onClick={toggleDarkMode}
            sx={{ color: themeColors.primary }}
          >
            {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {/* Notification icon — visible on ALL screen sizes */}
          <IconButton
            size="large"
            aria-label="notifications"
            color="inherit"
            sx={{ color: themeColors.primary }}
            onClick={() => history.push("/template/notifications")}
          >
            <Badge badgeContent={notificationCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* Desktop-only: account icon + username */}
          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{ color: themeColors.primary }}
              >
                <AccountCircle />
              </IconButton>
              <Typography
                variant="h9"
                noWrap
                component="div"
                style={{ color: themeColors.textColor, marginLeft: "8px" }}
              >
                Logged in as {user}
              </Typography>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      {renderMenu}

      {/* Mobile drawer - temporary */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: themeColors.drawerBg,
              color: themeColors.textColor,
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Desktop drawer - permanent */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: themeColors.drawerBg,
            color: themeColors.textColor,
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box className={classes.page} component="main" sx={{ flexGrow: 1, p: 3 }}>
        {children}
      </Box>

      {/* New notification popup — appears below the bell icon */}
      {showPopup && popupNotification && (
        <Box
          sx={{
            position: "fixed",
            top: { xs: "56px", sm: "64px" },
            right: "8px",
            zIndex: (theme) => theme.zIndex.drawer + 2,
            width: { xs: "calc(100vw - 16px)", sm: "340px" },
            maxWidth: "340px",
            "@keyframes notifSlideDown": {
              from: { opacity: 0, transform: "translateY(-12px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
            animation: "notifSlideDown 0.3s ease forwards",
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 2,
              borderRadius: 2,
              borderLeft: `4px solid ${themeColors.primary}`,
              bgcolor: "background.paper",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Close button */}
            <IconButton
              size="small"
              onClick={handleClosePopup}
              sx={{ position: "absolute", top: 6, right: 6 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* Content */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, pr: 3 }}>
              <NotificationsIcon sx={{ color: themeColors.primary, mt: 0.25, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                  New Notification
                </Typography>
                {popupNotification.type && (
                  <Chip
                    label={popupNotification.type.replace(/_/g, " ")}
                    size="small"
                    color="primary"
                    sx={{ mb: 0.75, height: 20, fontSize: "0.65rem", fontWeight: 600 }}
                  />
                )}
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word", lineHeight: 1.4 }}>
                  {popupNotification.message}
                </Typography>
              </Box>
            </Box>

            {/* Auto-dismiss progress bar */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                height: "3px",
                bgcolor: themeColors.primary,
                "@keyframes notifProgress": {
                  from: { width: "100%" },
                  to: { width: "0%" },
                },
                animation: "notifProgress 4.5s linear forwards",
              }}
            />
          </Paper>
        </Box>
      )}
    </Box>
  );
}

export default Template;
